import { useSyncExternalStore } from "react";
import manifest from "@/content/tutorial/circle-dance-v1.json";
import {
	artistPublicAssetURL,
	browserArtistRuntimeConfiguration,
} from "@/domain/auth/AuthProviderConfiguration";
import { artistSupabase } from "@/infrastructure/supabase/client";

export const BEGINNER_TUTORIAL_STEPS = [
	"inactive",
	"intro",
	"selectMusicPrompt",
	"generateMusic",
	"openLibrary",
	"openTutorialTrack",
	"imagineIntro",
	"selectImage",
	"focusImagePrompt",
	"generateImage",
	"selectFirstAsset",
	"selectVideo",
	"focusCircleVideoPrompt",
	"generateCircleVideo",
	"selectSecondAsset",
	"focusCloseUpVideoPrompt",
	"generateCloseUpVideo",
	"selectThirdAsset",
	"expandContinents",
	"selectSecondContinent",
	"openSecondSlotAssets",
	"placeSecondAsset",
	"selectThirdContinent",
	"openThirdSlotAssets",
	"placeThirdAsset",
	"saveComposition",
	"completion",
] as const;

export type BeginnerTutorialStep = (typeof BEGINNER_TUTORIAL_STEPS)[number];
export type CircleDanceTutorialAsset = "image" | "circleVideo" | "closeUpVideo";

export interface BeginnerTutorialState {
	readonly step: BeginnerTutorialStep;
	readonly simulatedCredits: number;
	readonly completed: boolean;
	readonly localTrackCreated: boolean;
	readonly generatedAssetCount: number;
	readonly assignedSlots: readonly number[];
	readonly claimedTrackId: string | null;
	readonly sessionBannerDismissed: boolean;
	readonly confirmationVisible: boolean;
	readonly errorMessage: string | null;
}
interface PersistedTutorialState {
	readonly completed?: boolean;
	readonly localTrackCreated?: boolean;
	readonly generatedAssetCount?: number;
	readonly assignedSlots?: readonly number[];
	readonly claimedTrackId?: string | null;
}

type HostedAsset = {
	readonly bucket: string;
	readonly object_path: string;
	readonly width?: number;
	readonly height?: number;
	readonly duration_seconds?: number;
};

type TutorialManifest = {
	readonly tutorial_key: string;
	readonly template_track_id: string;
	readonly starting_simulated_credits: number;
	readonly prompts: Readonly<Record<string, Readonly<Record<string, string>>>>;
	readonly hosted_assets: {
		readonly cover: HostedAsset;
		readonly audio_lossless: HostedAsset;
		readonly continent_1: HostedAsset;
		readonly continent_2: HostedAsset;
		readonly continent_3: HostedAsset;
	};
};

const content = manifest as TutorialManifest;
const tutorialRuntime = browserArtistRuntimeConfiguration();

function publicAsset(asset: HostedAsset): HostedAsset & { readonly public_url: string } {
	return {
		...asset,
		public_url: artistPublicAssetURL(tutorialRuntime, asset),
	};
}
const STORAGE_KEY = "artist.beginner-tutorial.circle-dance.v1";
const listeners = new Set<() => void>();

function readPersisted(): PersistedTutorialState {
	if (typeof window === "undefined") return {};
	try {
		return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}") as PersistedTutorialState;
	} catch {
		return {};
	}
}

const persisted = readPersisted();
let state: BeginnerTutorialState = {
	step: "inactive",
	simulatedCredits: content.starting_simulated_credits,
	completed: persisted.completed === true,
	localTrackCreated: persisted.localTrackCreated === true,
	generatedAssetCount: Math.max(0, persisted.generatedAssetCount ?? 0),
	assignedSlots: Array.isArray(persisted.assignedSlots)
		? [...new Set(persisted.assignedSlots.filter((slot) => [1, 2, 3].includes(slot)))].sort()
		: [],
	claimedTrackId: typeof persisted.claimedTrackId === "string" ? persisted.claimedTrackId : null,
	sessionBannerDismissed: false,
	confirmationVisible: false,
	errorMessage: null,
};

function emit(): void {
	for (const listener of listeners) listener();
}

function persist(): void {
	if (typeof window === "undefined") return;
	window.localStorage.setItem(
		STORAGE_KEY,
		JSON.stringify({
			completed: state.completed,
			localTrackCreated: state.localTrackCreated,
			generatedAssetCount: state.generatedAssetCount,
			assignedSlots: state.assignedSlots,
			claimedTrackId: state.claimedTrackId,
		}),
	);
}

function update(next: Partial<BeginnerTutorialState>, shouldPersist = false): void {
	state = { ...state, ...next };
	if (shouldPersist) persist();
	emit();
}

export const circleDanceTutorial = {
	key: content.tutorial_key,
	templateTrackId: content.template_track_id,
	localTrackId: "tutorial-local-circle-dance-v1",
	initialCredits: content.starting_simulated_credits,
	assets: {
		cover: publicAsset(content.hosted_assets.cover),
		audio: publicAsset(content.hosted_assets.audio_lossless),
		image: publicAsset(content.hosted_assets.continent_1),
		circleVideo: publicAsset(content.hosted_assets.continent_2),
		closeUpVideo: publicAsset(content.hosted_assets.continent_3),
	},
} as const;

function localeKey(): "en" | "zh-Hans" | "zh-Hant" {
	if (typeof navigator === "undefined") return "en";
	const language = navigator.language.toLowerCase();
	if (!language.startsWith("zh")) return "en";
	return language.includes("tw") || language.includes("hk") || language.includes("mo") || language.includes("hant")
		? "zh-Hant"
		: "zh-Hans";
}

export function tutorialPrompt(kind: "music" | "image" | "video_circle" | "video_close_up"): string {
	const values = content.prompts[kind];
	return values?.[localeKey()] ?? values?.en ?? "";
}

export function tutorialText(zhHans: string, zhHant: string, english: string): string {
	switch (localeKey()) {
		case "zh-Hans": return zhHans;
		case "zh-Hant": return zhHant;
		case "en": return english;
	}
}

export function tutorialGuideText(step = state.step): string {
	const texts: Record<BeginnerTutorialStep, readonly [string, string, string]> = {
		inactive: ["", "", ""],
		intro: ["本教学将带您创作一首协作式歌曲。", "本教學將帶您創作一首協作式歌曲。", "This tutorial guides you through creating a collaborative song."],
		selectMusicPrompt: ["第一步：点按 Circle Dance 推荐图片。", "第一步：點按 Circle Dance 推薦圖片。", "First, tap the Circle Dance prompt card."],
		generateMusic: ["现在点按“生成音乐”。本次只使用模拟灵感。", "現在點按「生成音樂」。本次只使用模擬靈感。", "Now tap Generate Music. This uses simulated inspo only."],
		openLibrary: ["歌曲已加入作品。请打开“我的”。", "歌曲已加入作品。請打開「我的」。", "The song is in your works. Open Library."],
		openTutorialTrack: ["点按置顶的 Circle Dance，进入 Imagine。", "點按置頂的 Circle Dance，進入 Imagine。", "Tap the pinned Circle Dance track to open Imagine."],
		imagineIntro: ["Imagine 可以为歌曲增加图片与视频，让作品更有趣。", "Imagine 可以為歌曲增加圖片與影片，讓作品更有趣。", "Imagine adds pictures and videos to make a song more expressive."],
		selectImage: ["选择“图片”。", "選擇「圖片」。", "Choose Image."],
		focusImagePrompt: ["点按提示词框，教学会自动填入图片提示词。", "點按提示詞框，教學會自動填入圖片提示詞。", "Tap the prompt box to insert the image prompt."],
		generateImage: ["生成第一张 Circle Dance 图片。", "生成第一張 Circle Dance 圖片。", "Generate the first Circle Dance image."],
		selectFirstAsset: ["点按刚刚生成的图片。", "點按剛剛生成的圖片。", "Tap the image you just generated."],
		selectVideo: ["再增加内容：选择“视频”。", "再增加內容：選擇「影片」。", "Add more: choose Video."],
		focusCircleVideoPrompt: ["点按提示词框，填入围圈跳舞的视频提示词。", "點按提示詞框，填入圍圈跳舞的影片提示詞。", "Tap the prompt box to insert the circle-dance video prompt."],
		generateCircleVideo: ["生成第二个媒体。它会沿第一个大陆的横轴加入。", "生成第二個媒體。它會沿第一個大陸的橫軸加入。", "Generate the second asset on the first continent's x-axis."],
		selectSecondAsset: ["点按第二个视频。", "點按第二個影片。", "Tap the second video."],
		focusCloseUpVideoPrompt: ["再次点按提示词框，填入特写提示词。", "再次點按提示詞框，填入特寫提示詞。", "Tap the prompt box again to insert the close-up prompt."],
		generateCloseUpVideo: ["生成第三个视频。", "生成第三個影片。", "Generate the third video."],
		selectThirdAsset: ["点按第三个视频。", "點按第三個影片。", "Tap the third video."],
		expandContinents: ["展开大陆槽位。", "展開大陸槽位。", "Expand the continent slots."],
		selectSecondContinent: ["选择第二个大陆槽位。", "選擇第二個大陸槽位。", "Select the second continent slot."],
		openSecondSlotAssets: ["打开“更多”，再选择“我的资产”。", "打開「更多」，再選擇「我的資產」。", "Open More, then My Assets."],
		placeSecondAsset: ["选择第二个视频。", "選擇第二個影片。", "Choose the second video."],
		selectThirdContinent: ["选择第三个大陆槽位。", "選擇第三個大陸槽位。", "Select the third continent slot."],
		openThirdSlotAssets: ["再次打开“更多”与“我的资产”。", "再次打開「更多」與「我的資產」。", "Open More and My Assets again."],
		placeThirdAsset: ["选择第三个视频。", "選擇第三個影片。", "Choose the third video."],
		saveComposition: ["点按右上角“保存”。本次保存仅发生在本地。", "點按右上角「儲存」。本次儲存僅發生在本地。", "Tap Save in the top-right. This tutorial save stays in the browser."],
		completion: ["新手教学已完成！您现在拥有一首协作式歌曲。", "新手教學已完成！您現在擁有一首協作式歌曲。", "Tutorial complete! You now have a collaborative song."],
	};
	const [zhHans, zhHant, english] = texts[step];
	return tutorialText(zhHans, zhHant, english);
}

export const beginnerTutorialStore = {
	getSnapshot: () => state,
	subscribe(listener: () => void) {
		listeners.add(listener);
		return () => listeners.delete(listener);
	},
	requestStart: () => update({ confirmationVisible: true }),
	cancelStart: () => update({ confirmationVisible: false }),
	begin: () => update({ step: "intro", simulatedCredits: content.starting_simulated_credits, confirmationVisible: false, errorMessage: null }),
	exit: () => update({ step: "inactive", confirmationVisible: false, errorMessage: null }),
	advance(expected: BeginnerTutorialStep, next: BeginnerTutorialStep) {
		if (state.step === expected) update({ step: next });
	},
	dismissHomeTask: () => update({ sessionBannerDismissed: true }),
	createLocalTrack: () => update({ localTrackCreated: true }, true),
	debit(amount: number): boolean {
		const normalized = Math.max(0, Math.trunc(amount));
		if (state.simulatedCredits < normalized) {
			update({ errorMessage: tutorialText("模拟灵感不足。", "模擬靈感不足。", "Not enough simulated inspo.") });
			return false;
		}
		update({ simulatedCredits: state.simulatedCredits - normalized });
		return true;
	},
	recordGenerated(asset: CircleDanceTutorialAsset) {
		const count = asset === "image" ? 1 : asset === "circleVideo" ? 2 : 3;
		update({ generatedAssetCount: Math.max(state.generatedAssetCount, count) }, true);
	},
	assignSlot(slot: number) {
		if (![1, 2, 3].includes(slot)) return;
		update({ assignedSlots: [...new Set([...state.assignedSlots, slot])].sort() }, true);
	},
	async complete(): Promise<void> {
		update({
			completed: true,
			localTrackCreated: true,
			generatedAssetCount: Math.max(state.generatedAssetCount, 3),
			assignedSlots: [1, 2, 3],
			step: "completion",
		}, true);
		try {
			await artistSupabase().rpc("complete_beginner_tutorial", {
				p_tutorial_key: circleDanceTutorial.key,
				p_client_platform: "web",
			});
		} catch {
			// Device completion remains authoritative until the next authenticated retry.
		}
	},
	dismissCompletion: () => {
		if (state.step === "completion") update({ step: "inactive" });
	},
	async claimForRealGeneration(): Promise<string> {
		if (state.claimedTrackId !== null) return state.claimedTrackId;
		const { data, error } = await artistSupabase().rpc("claim_beginner_tutorial_track", {
			p_tutorial_key: circleDanceTutorial.key,
			p_client_platform: "web",
		});
		if (error) throw error;
		if (typeof data !== "string" || data.length === 0) throw new Error("tutorial-claim-response-invalid");
		update({ claimedTrackId: data }, true);
		return data;
	},
};

export function useBeginnerTutorial(): BeginnerTutorialState {
	return useSyncExternalStore(
		beginnerTutorialStore.subscribe,
		beginnerTutorialStore.getSnapshot,
		beginnerTutorialStore.getSnapshot,
	);
}
